import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { useEffect, useRef, useState } from 'react';
import Flickity from 'react-flickity-component';

import { stopoverTourOptionPropTypes } from './PropTypes';

import { currencyDepositAndTotalPricePropTypes } from '../../../util/currency';
import Icon from '../Atoms/Icon';
import UpgradeCard from '../BookingFieldset/UpgradeCard';
import { portalNodeType } from '../../../util/sharedPropTypes';

const propTypes = exact({
  currencyPriceSetting: currencyDepositAndTotalPricePropTypes,
  currencySymbol: PropTypes.string.isRequired,
  groupKey: PropTypes.string.isRequired,
  handleFieldSelect: PropTypes.func.isRequired,
  handleFieldRemove: PropTypes.func,
  options: PropTypes.arrayOf(stopoverTourOptionPropTypes).isRequired,
  useRemoveButton: PropTypes.bool,
  viewDetailsCtaText: PropTypes.string,
  portalNode: portalNodeType,
  perPersonText: PropTypes.string.isRequired,
});

const defaultProps = {
  handleFieldRemove: () => {},
  useRemoveButton: false,
  viewDetailsCtaText: 'View Details',
  portalNode: null,
};

function OptionsCarousel(props) {
  const [slidesCount, setSlidesCount] = useState(false);
  const [selectedSlideIndex, setSelectedSlideIndex] = useState(0);
  const flickityRef = useRef();

  const checkToUpdateUI = () => {
    setSlidesCount(flickityRef.current?.slides.length);
    setSelectedSlideIndex(flickityRef.current.selectedIndex);
    flickityRef.current.resize();
  };

  const flickityRefFunc = (ref) => {
    flickityRef.current = ref;
    flickityRef.current.on('ready', checkToUpdateUI);
    flickityRef.current.on('change', checkToUpdateUI);
  };

  useEffect(() => {
    const handleWindowResize = debounce(checkToUpdateUI, 1000 / 6);
    window.addEventListener('resize', handleWindowResize, { passive: true });
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [checkToUpdateUI]);

  const {
    groupKey,
    handleFieldRemove,
    handleFieldSelect,
    currencySymbol,
    perPersonText,
    portalNode,
  } = props;

  return (
    <div>
      <div
        className={`UpgradeGroup_flickityCarousel FlickityCarousel${
          slidesCount === 1 ? ' FlickityCarousel-hideDots' : ''
        }`}
      >
        <Flickity
          className="FlickityCarousel_flickity"
          flickityRef={flickityRefFunc}
          options={{
            cellAlign: 'left',
            contain: true,
            draggable: true,
            friction: 0.8,
            groupCells: true,
            initialIndex: 0,
            pageDots: true,
            prevNextButtons: false,
            selectedAttraction: 0.2,
          }}
        >
          {props.options.map(
            ({
              description,
              isSelected,
              key,
              limitedAvailability,
              limitedAvailabilityText,
              modalProps,
              price,
              singleImageArray,
              soldOut,
              soldOutText,
              subtitle,
              title,
            }) => (
              <div className="FlickityCarousel_cell" key={key}>
                <UpgradeCard
                  blockName="UpgradeGroup_card"
                  closeModalText={props.closeModalText}
                  currencySymbol={currencySymbol}
                  description={description}
                  forceVerticalLayout
                  handleClick={() => handleFieldSelect(groupKey, key)}
                  handleRemoveClick={() => handleFieldRemove(groupKey, key)}
                  images={singleImageArray}
                  isSelected={isSelected}
                  limitedAvailability={limitedAvailability}
                  limitedAvailabilityText={limitedAvailabilityText}
                  modalProps={{ ...modalProps, portalNode }}
                  perPersonText={perPersonText}
                  price={price}
                  soldOut={soldOut}
                  soldOutText={soldOutText}
                  strapLine={subtitle}
                  title={title || 'title missing'}
                  useRemoveButton={props.useRemoveButton}
                  viewDetailsCtaText={props.viewDetailsCtaText}
                />
              </div>
            )
          )}
        </Flickity>
        {slidesCount > 1 && (
          <div className="FlickityCarousel_sideButtons">
            <button
              type="button"
              className={`FlickityCarousel_leftButton${
                selectedSlideIndex === 0 ? ' FlickityCarousel_leftButton-disabled' : ''
              }`}
              onClick={() => flickityRef.current?.previous()}
            >
              <Icon id="arrow-left" />
            </button>
            <button
              type="button"
              className={`FlickityCarousel_rightButton${
                selectedSlideIndex + 1 === slidesCount
                  ? ' FlickityCarousel_rightButton-disabled'
                  : ''
              }`}
              onClick={() => flickityRef.current?.next()}
            >
              <Icon id="arrow-right" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

OptionsCarousel.propTypes = propTypes;
OptionsCarousel.defaultProps = defaultProps;

export default OptionsCarousel;
